import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import io from "socket.io-client";
import { API_BASE, fetchWithAuth } from "../utils/api";
import { SOCKET_URL } from "../utils/constants";

export function useRevenueRows(user) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const socketRef = useRef(null);

  const normalizedRole = useMemo(
    () => user?.role?.toLowerCase(),
    [user?.role]
  );
  const isDriver = normalizedRole === "driver";

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchWithAuth(`${API_BASE}/revenue/rows`);
      const data = await res.json();
      if (!data.success) {
        throw new Error(data.message || "Không thể tải dữ liệu");
      }
      setRows(data.data || []);
    } catch (err) {
      setError(err.message || "Có lỗi xảy ra");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || isDriver) {
      setRows([]);
      return;
    }
    loadData();
  }, [user, isDriver, loadData]);

  useEffect(() => {
    if (!user || isDriver) return;

    const socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ["websocket"],
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("Revenue socket connected:", socket.id);
    });

    socket.on("disconnect", () => {
      console.log("Revenue socket disconnected");
    });

    socket.on("connect_error", (error) => {
      console.error("Revenue socket connection error:", error);
    });

    socket.on("revenue:accounting-updated", (payload) => {
      console.log("Received revenue:accounting-updated event", payload);
      // Nếu event đến từ chính user hiện tại thì bỏ qua
      if (payload?.sourceUserId && user?.id && payload.sourceUserId === user.id) {
        return;
      }
      
      // Chỉ cập nhật row cụ thể thay vì reload toàn bộ
      if (payload?.rowIndex) {
        setRows((prevRows) => {
          const rowIndex = payload.rowIndex;
          const updatedRow = {
            rowIndex: payload.rowIndex,
            ngay: payload.ngay ?? '',
            laiXe: payload.laiXe ?? '',
            soXe: payload.soXe ?? '',
            dieuPhoi: payload.dieuPhoi ?? '',
            doanhThu: payload.doanhThu ?? 0,
            diemThuc: payload.diemThuc ?? '',
            chanCong: payload.chanCong ?? 0,
            keToanChuyen: payload.keToanChuyen ?? '',
            doiChieuNote: payload.doiChieuNote ?? '',
            noteNhap: payload.noteNhap ?? '',
          };
          
          // Tìm và cập nhật row tương ứng
          const index = prevRows.findIndex((r) => r.rowIndex === rowIndex);
          if (index !== -1) {
            const newRows = [...prevRows];
            newRows[index] = updatedRow;
            return newRows;
          }
          
          // Nếu không tìm thấy row (có thể là row mới), reload toàn bộ
          return prevRows;
        });
      } else {
        // Nếu không có đủ thông tin, reload toàn bộ
        loadData();
      }
    });

    return () => {
      socket.off("revenue:accounting-updated");
      socket.off("connect");
      socket.off("disconnect");
      socket.off("connect_error");
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user, isDriver, loadData]);

  return { rows, setRows, loading, error, setError, loadData };
}

